import React from 'react';
import { useLocation } from 'react-router-dom';
import { Play, Pause, Square, Clock, Bell, BellOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTimer } from '@/context/TimerContext';

/**
 * A floating timer that appears on all pages (except /tasks) when the timer is running,
 * has an alarm set, or is expired/triggered.
 */
const GlobalFloatingTimer: React.FC = () => {
  const location = useLocation();
  const {
    timer,
    isFlashing,
    startTimer,
    pauseTimer,
    stopTimer,
    dismissAlarm,
    cancelScheduledAlarm,
    formatTimeDisplay,
  } = useTimer();

  // Don't show on the tasks page since it already has the timer
  if (location.pathname === '/tasks') {
    return null;
  }

  const isAlarming = timer.isExpired || timer.isAlarmTriggered;
  const isActive = timer.isRunning || timer.isAlarmSet || isAlarming;

  // Only show when timer is active
  if (!isActive) {
    return null;
  }

  // Convert 24-hour to 12-hour format for display
  const formatTo12Hour = (time24: string) => {
    const [hours, mins] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    let hour12 = hours % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Calculate time until scheduled alarm
  const getTimeUntilAlarm = () => {
    if (!timer.scheduledTime) return null;
    const [hours, mins] = timer.scheduledTime.split(':').map(Number);
    const now = new Date();
    const alarm = new Date();
    alarm.setHours(hours, mins, 0, 0);
    
    if (alarm <= now) {
      alarm.setDate(alarm.getDate() + 1);
    }
    
    const diff = Math.floor((alarm.getTime() - now.getTime()) / 1000 / 60);
    if (diff < 60) return `${diff}m`;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h ${m}m`;
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100]">
      <div className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl border transition-all duration-300",
        isAlarming && isFlashing 
          ? "bg-red-50 border-red-300 ring-2 ring-red-400 shadow-red-200" 
          : "bg-white border-gray-200 shadow-gray-200/50"
      )}>
        {/* Icon */}
        {timer.isAlarmSet ? (
          <Bell className={cn(
            "w-5 h-5",
            isAlarming ? "text-red-500" : "text-amber-500"
          )} />
        ) : (
          <Clock className={cn(
            "w-5 h-5",
            isAlarming ? "text-red-500" : "text-emerald-600"
          )} />
        )}
        
        {/* Time Display */}
        <div className="flex flex-col">
          <span className={cn(
            "text-lg font-mono font-bold leading-none",
            isAlarming ? "text-red-600" : timer.isRunning ? "text-emerald-600" : "text-gray-900"
          )}>
            {timer.isAlarmSet && timer.scheduledTime ? formatTo12Hour(timer.scheduledTime) : formatTimeDisplay(timer.totalSeconds)}
          </span>
          {timer.isAlarmSet && !isAlarming && (
            <span className="text-[10px] text-gray-500 leading-none mt-0.5">
              in {getTimeUntilAlarm()}
            </span>
          )}
        </div>
        
        {/* Control Buttons */}
        {!isAlarming && (
          <div className="flex items-center gap-1 border-l border-gray-200 pl-3 ml-1">
            {!timer.isAlarmSet && (
              <>
                {timer.isRunning ? (
                  <button
                    onClick={pauseTimer}
                    className="p-2 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                    title="Pause"
                  >
                    <Pause className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={startTimer}
                    className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors"
                    title="Resume"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={stopTimer}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                  title="Reset"
                >
                  <Square className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            
            {timer.isAlarmSet && (
              <button
                onClick={cancelScheduledAlarm}
                className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                title="Cancel Alarm"
              >
                <BellOff className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        
        {/* Dismiss when alarming */}
        {isAlarming && (
          <button
            onClick={dismissAlarm}
            className="px-4 py-1.5 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium transition-colors"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

export default GlobalFloatingTimer;
